import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import AurProgressBar from '../common/aur-progressbar/AurProgressBar';
import {Box} from '@mui/material';
import AurPageHeader from '../common/aur-page-header/AurPageHeader';
import AurPopup from '../common/aur-popup/AurPopup';
import AurButton from '../common/aur-button/AurButton';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {t} from 'i18next';
import {bulkUploadUsersFileProcessing} from '../../api/UserManagementService';
import {
  setFileProcessError,
  setFileUploadBatchId,
  setSelectedClient,
  setDuplicateEmail,
  setInitialUploadPage,
  clearInitialUploadPage,
} from '../../redux/slices/bulkUserFileNameSlice';
import AurIcon, {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import BulkUserDownloadTemplate from './BulKUserDownloadTemplate';
import {SSE_ENDPOINT} from '../../constants/constants';
import {UserInfoType} from '../../redux/slices/userSlice';
import {setFixErrorTab, setValue, setSummary} from '../../redux/slices/bulkUserEditSlice';
import {resetState, setUsers} from '../../redux/slices/bulkUserResultSlice';
import {UrlPathEnums} from '../../utils/enumUtils';
import {useAnalytics} from '../../hooks/useAnalytics';
import {
  GA_EVENT_NAMES,
  GA_CLICK_TYPES,
  GA_REGIONS,
} from '../../constants/analyticsConstants';

const AurBulkUserFileProcessing = () => {
  const navigate = useNavigate();
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [errorUploadModal, setErrorUploadFile] = useState<boolean>(false);
  const [triggerCall, setTriggerCall] = useState<boolean>(false);
  const [messages, setMessages] = useState<number>(0);
  const selectedFile = useSelector(
    (state: RootState) => state.bulkUserFile.file
  );
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const userData: UserInfoType | null = useSelector(
    (state: RootState) => state.user.user
  );
  const userId = userData?.userId;
  const bulkUserFileName: string = useSelector(
    (state: RootState) => state.bulkUserFile.fileName
  );

  const selectedClientName = useSelector(
    (state: RootState) => state.bulkUserFile.selectedClient
  );
  const duplicateEmail = useSelector(
    (state: RootState) => state.bulkUserFile.duplicateEmail
  );

  useEffect(() => {
    if (userId) {
      const eventSource = new EventSource(`${SSE_ENDPOINT}/${userId}`);
      dispatch(clearInitialUploadPage());
      dispatch(resetState());
      dispatch(setUsers([]));
      dispatch(setSummary({}));
      dispatch(setFileUploadBatchId(''));
      eventSource.onmessage = (event) => {
        const responseData = JSON.parse(event.data);
        const percentage = Number(responseData.PercentageProcessed) || 0;
        setMessages((prev) => Math.max(prev, percentage));
        if (responseData.BatchJobImportId) {
          dispatch(setFileUploadBatchId(responseData.BatchJobImportId));
        }
      };
      eventSource.onerror = (error) => {
        console.error('EventSource failed:', error);
        eventSource.close();
      };
      setTriggerCall(true);
      return () => {
        eventSource.close();
      };
    }
  }, [userId, dispatch]);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).bulkUploadClientId;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setErrorUploadFile(false);
        const bulkUploadUserResponse = await bulkUploadUsersFileProcessing(
          selectedFile as File,
          clientId
        );
        const responseBody = bulkUploadUserResponse?.data;
        const uploadData = responseBody?.data ?? responseBody;
        if (uploadData?.stagingHeaderId) {
          dispatch(setFileUploadBatchId(uploadData.stagingHeaderId));
        }
        setMessages((prev) => Math.max(prev, 100));
        const initialUpload =
          uploadData?.initialUpload ?? uploadData?.InitialUpload;
        if (initialUpload) {
          const summary = initialUpload.summary ?? initialUpload.Summary ?? {};
          dispatch(
            setInitialUploadPage({
              users: initialUpload.users ?? initialUpload.Users ?? [],
              summary,
            })
          );
          dispatch(setSummary(summary));
        }
        navigate(UrlPathEnums.UsersBulkUploadResults);
        dispatch(setFixErrorTab(false));
        dispatch(setValue(0));
      } catch (error: any) {
        if (
          error.message === t('common.errorUploadModal.networkError') ||
          error.status === 500
        ) {
          setErrorUploadFile(true);
          setTriggerCall(false);
        }

        if (
          error.status === 400 &&
          error?.response?.data?.meta?.message.includes(
            t('common.bulkUploadModule.duplicateEmail')
          )
        ) {
          handleBackNavigation();
          dispatch(setDuplicateEmail(true));
          dispatch(setFileProcessError(true));
          dispatch(setSelectedClient(selectedClientName));
        }
      }
    };

    if (selectedFile && triggerCall) {
      fetchData();
    }
  }, [selectedFile, triggerCall, clientId]);

  const handleBackNavigation = () => {
    navigate(UrlPathEnums.UsersBulkUploadAdd);
    if (!duplicateEmail) {
      dispatch(setSelectedClient(''));
    } else {
      dispatch(setSelectedClient(selectedClientName));
    }
  };
  const navigateToBulkUploadScreen = () => {
    navigate(UrlPathEnums.UsersBulkUploadAdd);
    setCancelModal(false);
    if (!duplicateEmail) {
      dispatch(setSelectedClient(''));
    } else {
      dispatch(setSelectedClient(selectedClientName));
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.cancelUpload'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.users.cancelUploadAction'),
      },
    });
  };

  const handleCancel = () => {
    setCancelModal(true);
  };

  const closeModalHandler = () => {
    setCancelModal(false);
  };
  const reuploadFile = () => {
    setTriggerCall(true);
  };

  return (
    <div aria-label={t('common.aurUploadBox.ariaLableAssertive')}>
      <Box
        className="bulkUpload-bg-Color upload-mainContainer"
        aria-label={t('common.aurUploadBox.ariaLableAssertive')}
      >
        <div className="custom-header">
          <AurPageHeader
            heading={t('common.aurUploadBox.addTeamMemberText')}
            onLinkCLick={handleBackNavigation}
            linkLabel={t('common.aurUploadBox.manageTeamMembers')}
            selectedClientName={selectedClientName}
          />
        </div>
        <Box
          className="file-processing-container"
          aria-label={t('common.aurUploadBox.ariaLableAssertive')}
        >
          <AurProgressBar value={messages} fileName={bulkUserFileName}>
            <AurButton
              classNames="aur-progress-bar_cancel-button"
              label={t('buttons.cancelUpload')}
              onClick={handleCancel}
              type="exit"
              size="Medium"
            />
          </AurProgressBar>
        </Box>
        <BulkUserDownloadTemplate />

        {cancelModal && (
          <AurPopup
            className="aur-unsaved-changes-popup"
            open={cancelModal}
            onOk={navigateToBulkUploadScreen}
            onCancel={closeModalHandler}
            popupTitle={t('common.cancelUploadPopup.title')}
            popupHeading={t('common.cancelUploadPopup.heading')}
            showPopupHeading={true}
            showPopupTitle={true}
            showCloseIcon={false}
            popupContent={t('common.cancelUploadPopup.content')}
            labelActionCancel={t('common.cancelUploadPopup.buttonStay')}
            labelActionOk={t('common.cancelUploadPopup.buttonYes')}
            exitButtonHint={t('common.cancelUploadPopup.exitButtonHint')}
            continueButtonHint={t(
              'common.cancelUploadPopup.continueButtonHint'
            )}
          />
        )}

        {errorUploadModal && (
          <AurPopup
            className="inactivity-popup-modal errorUpload-popup"
            open={errorUploadModal}
            onOk={reuploadFile}
            onCancel={navigateToBulkUploadScreen}
            popupHeading={t('common.errorUploadModal.heading')}
            showPopupHeading={true}
            showCloseIcon={false}
            popupContent={t('common.errorUploadModal.content')}
            labelActionCancel={t('common.errorUploadModal.buttonStay')}
            labelActionOk={t('common.errorUploadModal.buttonYes')}
            headingIcon={
              <AurIcon
                icon={IconNames.Error}
                color={IconColors.Critical}
                customClassName="heading-warning-icon"
              />
            }
            popupContentClass="popup-content"
          />
        )}
      </Box>
    </div>
  );
};

export default AurBulkUserFileProcessing;
